import { useState, useEffect } from "react";
import {
  DocumentProcessingError,
  processUploadedDocumentPhoto,
} from "../../../services/document-sdk/process-updated-document-photo";
import { logPassportPhotoAccepted, logPassportPhotoRejected } from "../passportScanDiagnostics";
import { chooseMrzCrop, mrzCropPolygon, readImageSize } from "../mrzCrop";
import { errorClassName, upscaleMrzStrip } from "../mrzUpscale";

function filterMRZNoise(mrzTextArray) {
  const expectedLength = 44;
  const filteredArray = mrzTextArray.filter((element) => element.length === expectedLength);

  if (filteredArray.length > 2) {
    return filteredArray.slice(0, 2); // Take only the first two if more than two match the length
  }

  return filteredArray;
}

// One OCR read, split into lines. The text never leaves this file (#244).
async function readOcrLines(ocr, image) {
  const ocrResult = await ocr.performOcr(image);
  const ocrText = ocrResult?.ocrElement?.text;
  return typeof ocrText === "string" ? ocrText.split("\n") : [];
}

// #245: the OCR reads the MRZ strip at 2x first. If that read does not give two 44-character
// lines, or there is no 2x image, it reads the strip as-is, as before. The lines always come
// from one whole read: never trimmed, mixed between reads or rebuilt. `diagnostic` is
// log-safe: numbers, fixed words and an error class name.
async function readMrzLines(ocr, strip) {
  const upscaled = await upscaleMrzStrip(strip);
  const diagnostic = { mrzOcrScale: 1 };
  if (upscaled.scale === 1) {
    diagnostic.mrzUpscale = upscaled.skipped;
    if (upscaled.errorName) diagnostic.mrzUpscaleError = upscaled.errorName;
  } else {
    try {
      const upscaledLines = await readOcrLines(ocr, upscaled.image);
      const upscaledMrz = filterMRZNoise(upscaledLines);
      if (upscaledMrz.length === 2) {
        return { ocrLines: upscaledLines, mrzTextArray: upscaledMrz, diagnostic: { mrzOcrScale: upscaled.scale } };
      }
      diagnostic.upscaledLineLengths = upscaledLines.slice(0, 10).map((line) => line.length);
    } catch (e) {
      diagnostic.mrzUpscale = "ocr_error";
      diagnostic.mrzUpscaleError = errorClassName(e);
    }
    diagnostic.mrzOcrFallback = true;
  }
  const ocrLines = await readOcrLines(ocr, strip);
  return { ocrLines, mrzTextArray: filterMRZNoise(ocrLines), diagnostic };
}

const UploadPhoto = (props) => {
  const { imageBuffer, validationID, onSuccess, onError } = props;

  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    if (imageBuffer) {
      detectDocument(imageBuffer);
    }
    if (imageBuffer) {
      const blob = new Blob([imageBuffer], { type: "image/jpeg" });
      const url = URL.createObjectURL(blob);
      setImageUrl(url);

      // Clean up the object URL when the component unmounts or when imageBuffer changes
      return () => URL.revokeObjectURL(url);
    }
  }, [imageBuffer]);

  const detectDocument = async (imageBuffer) => {
    let mrzCrop = null; // which MRZ strip ran, for the #244 events (#245)
    let photoSize = null; // original photo pixel size, for the #244 events (#245)
    let mrzRead = null; // which OCR read gave the result, for the #244 events (#245)
    try {
      photoSize = readImageSize(imageBuffer);
      const { result } = await processUploadedDocumentPhoto({
        imageBuffer,
        postprocess: async (ctx) => {
          mrzRead = null; // a withSdk retry runs postprocess again
          const imageProcessor = await ctx.sdk.createImageProcessor(ctx.croppedImageBuffer);

          let mrz = null;
          let ocr = null;

          try {
            mrzCrop = chooseMrzCrop(ctx.croppedImageBuffer);
            const normalizedMrzPoints = mrzCropPolygon(mrzCrop.mrzCropRatio);

            await imageProcessor.crop(normalizedMrzPoints);
            const mrzImage = await imageProcessor.processedImage();

            const mrzImageForMrz = mrzImage.slice(0);
            const mrzImageForOcr = mrzImage.slice(0);
            const mrzImageForArtifact = mrzImage.slice(0);

            ctx.artifacts.mrzImage = mrzImageForArtifact;

            mrz = await ctx.sdk.createSimpleMRZRecognizer();
            const mrzResult = await mrz.recognize(mrzImageForMrz);

            const hasMrzTextLikeFields =
              typeof mrzResult?.documentNumber?.value === "string" && mrzResult.documentNumber.value.trim().length > 0;
            const hasIdentityFields =
              (typeof mrzResult?.surname?.value === "string" && mrzResult.surname.value.trim().length > 0) ||
              (typeof mrzResult?.givenNames?.value === "string" && mrzResult.givenNames.value.trim().length > 0) ||
              (typeof mrzResult?.birthDate?.value === "string" && mrzResult.birthDate.value.trim().length > 0) ||
              (typeof mrzResult?.expiryDate?.value === "string" && mrzResult.expiryDate.value.trim().length > 0) ||
              (typeof mrzResult?.nationality?.value === "string" && mrzResult.nationality.value.trim().length > 0);
            const hasMrz = !!mrzResult && (hasMrzTextLikeFields || hasIdentityFields);

            if (!hasMrz) {
              throw new DocumentProcessingError("MRZ code not detected. Please retake the photo.", false, {
                reason: "mrz_zone_not_detected",
              });
            }

            ocr = await ctx.sdk.createOcrEngine();
            const { ocrLines, mrzTextArray, diagnostic } = await readMrzLines(ocr, mrzImageForOcr);
            mrzRead = diagnostic;

            if (mrzTextArray.length !== 2) {
              // Line lengths only, never the text (#244).
              throw new DocumentProcessingError(
                "We couldn't capture some of the details of your document. Please retake the photo.",
                false,
                {
                  reason: "mrz_lines_incomplete",
                  ocrLineCount: ocrLines.length,
                  ocrLineLengths: ocrLines.slice(0, 10).map((line) => line.length),
                }
              );
            }

            ctx.artifacts.mrzTextArray = mrzTextArray;
          } finally {
            if (mrz) {
              await mrz.release();
            }
            if (ocr) {
              await ocr.release();
            }
            await imageProcessor.release();
          }
        },
        buildResult: async (ctx) => {
          const croppedFile = new Blob([ctx.croppedImageBuffer], { type: "image/jpeg" });
          const originalFile = new Blob([ctx.originalImageBuffer], { type: "image/jpeg" });
          const mrzFile = new Blob([ctx.artifacts.mrzImage], { type: "image/jpeg" });
          const mrzTextArray = ctx.artifacts.mrzTextArray;

          return {
            croppedImageBlob: croppedFile,
            originalImageBlob: originalFile,
            mrzImageBlob: mrzFile,
            mrzTextArray: mrzTextArray,
          };
        },
      });

      logPassportPhotoAccepted(validationID, mrzCrop, photoSize, mrzRead);
      onSuccess(result);
    } catch (error) {
      logPassportPhotoRejected(error, validationID, mrzCrop, photoSize, mrzRead);
      if (error instanceof DocumentProcessingError) {
        return onError(error.message, error.canRetry);
      }

      return onError("We couldn’t get a clear read on the document. Try capturing the photo again.", false);
    }
  };

  return imageBuffer ? (
    <div className="fixed inset-0 flex items-center justify-center bg-black">
      {imageUrl && (
        <img src={imageUrl} alt="Uploaded" className="mx-auto my-auto max-w-full max-h-full object-contain filter" />
      )}
      {/* Scanning line overlay */}
      <div className="absolute inset-0">
        <div className="scanner" />
      </div>
      {/* Centered text overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-white font-semibold text-center">Please wait while we are processing your photo...</p>
      </div>
      <style jsx>{`
        .scanner {
          position: absolute;
          width: 100%;
          height: 4px; /* Line thickness */
          background: rgba(0, 255, 0, 0.8);
          animation: scan 2s linear infinite;
        }
        @keyframes scan {
          0% {
            top: 0;
          }
          100% {
            top: calc(100% - 4px);
          }
        }
      `}</style>
    </div>
  ) : (
    <div>Loading...</div>
  );
};

export default UploadPhoto;
